/**
 * Date Range Picker Component
 * Ant Design-style date range picker with two inputs and preset ranges
 */

'use client';

import * as React from 'react';
import { DateRangePicker as ReactDateRangePicker } from 'react-date-range';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Input } from '@/components/ui/input';
// import { Button } from '@/components/ui/button';
import { CalendarIcon } from 'lucide-react';
import { format, isAfter, startOfDay } from 'date-fns';
import { cn } from '@/lib/utils';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import './date-range.css';

interface DateRangePickerProps {
	value?: { startDate?: Date; endDate?: Date };
	onChange?: (range: { startDate?: Date; endDate?: Date }) => void;
	disabled?: (date: Date) => boolean;
	placeholder?: { start?: string; end?: string };
	className?: string;
	/** When true, renders as a normal inline flex element instead of absolute-positioned */
	inline?: boolean;
}

export function DateRangePicker({
	value,
	onChange,
	disabled,
	placeholder = { start: 'Start Date', end: 'End Date' },
	className,
	inline = false,
}: DateRangePickerProps) {
	const [isOpen, setIsOpen] = React.useState(false);
	const [calendarViewState, setCalendarViewState] = React.useState<'initial' | 'updated'>('initial');
	const [dateRange, setDateRange] = React.useState<{
		key: string;
		startDate: Date;
		endDate: Date;
	}>({
		key: 'selection',
		startDate: value?.startDate || new Date(),
		endDate: value?.endDate || new Date()
	});

	// Sync with external value
	React.useEffect(() => {
		if (value?.startDate || value?.endDate) {
			setDateRange({
				key: 'selection',
				startDate: value?.startDate || new Date(),
				endDate: value?.endDate || new Date()
			});
		}
	}, [value?.startDate, value?.endDate]);

	const isDateDisabled = React.useCallback(
		(date: Date) => {
			// Disable future dates
			if (isAfter(startOfDay(date), startOfDay(new Date()))) {
				return true;
			}
			// Custom disabled function
			if (disabled) {
				return disabled(date);
			}
			return false;
		},
		[disabled]
	);

	const isDateSame = (date1: Date, date2: Date) => date1.toDateString() === date2.toDateString();

	const handleDateSelect = (ranges: any) => {
		const selection = ranges.selection;

		if (!selection) {
			return;
		}

		// If in initial state and both dates are the same (first click)
		if (calendarViewState === 'initial' && isDateSame(selection.startDate, selection.endDate)) {
			// Update state to 'updated' but don't close
			setCalendarViewState('updated');
			setDateRange(selection);
		} else {
			// Second click or different dates - update and close
			setDateRange(selection);
			onChange?.({
				startDate: selection.startDate,
				endDate: selection.endDate
			});
			// Don't close if the user is typing in one of the "Days up to today" / "Days starting today" inputs
			const isInputRangeFocused = document.activeElement?.classList.contains('rdrInputRangeInput');
			if (!isInputRangeFocused) {
				setCalendarViewState('initial');
				setIsOpen(false);
			}
		}
	};

	const getDisplayText = () => {
		if (value?.startDate && value?.endDate && dateRange?.startDate && dateRange?.endDate) {
			// Check if it's a single date (same date for both)
			if (dateRange.startDate.getTime() === dateRange.endDate.getTime()) {
				return format(dateRange.startDate, 'MMM d, yyyy');
			}
			return `${format(dateRange.startDate, 'MMM d, yyyy')} - ${format(dateRange.endDate, 'MMM d, yyyy')}`;
		}
		return placeholder.start && placeholder.end ? `${placeholder.start} - ${placeholder.end}` : 'Select date range';
	};

	// The day-count boxes hold a derived value ("-" or a number), so typing next to it
	// concatenates ("-" + "2" reads as -2). Select on focus so a keystroke replaces it.
	const handleInputRangeFocus = (e: React.FocusEvent<HTMLDivElement>) => {
		const target = e.target;
		if (target instanceof HTMLInputElement && target.classList.contains('rdrInputRangeInput')) {
			target.select();
		}
	};

	const handleOpenChange = (open: boolean) => {
		setIsOpen(open);
		if (open) {
			// Reset state when opening
			setCalendarViewState('initial');
		}
	};

	const hasValue = value?.startDate && value?.endDate;

	return (
		<Popover open={isOpen} onOpenChange={handleOpenChange}>
			<PopoverTrigger asChild>
				{inline ? (
					<div className={cn('cursor-pointer flex items-center gap-1 rounded-full border-[1.5px] border-gray-300 pl-3 pr-1 py-1', className)}>
						<CalendarIcon className='h-4 w-4 shrink-0 text-black-100' />
						<Input
							readOnly
							placeholder={
								placeholder.start && placeholder.end ? `${placeholder.start} - ${placeholder.end}` : 'Select date range'
							}
							value={getDisplayText()}
							className={cn(
								'cursor-pointer text-center border-none shadow-none px-2 bg-gray-100 hover:bg-gray-200 text-xs h-6 min-w-[175px] rounded-full',
								!hasValue && 'text-muted-foreground'
							)}
						/>
					</div>
				) : (
					<div className={cn('relative flex-1', className)}>
						<div className='cursor-pointer absolute right-1 border-[1.5px] border-gray-300 rounded-full pl-4 py-1 pr-1 top-1/2 -translate-y-1/2 flex items-center gap-1'>
							<CalendarIcon className='h-5 w-5 text-black-100' />
							<Input
								readOnly
								placeholder={
									placeholder.start && placeholder.end ? `${placeholder.start} - ${placeholder.end}` : 'Select date range'
								}
								value={getDisplayText()}
								className={cn(
									'cursor-pointer text-center border-none shadow-none ml-1.5 px-2 bg-gray-100 hover:bg-gray-200 text-xs h-6 min-w-[175px] rounded-full',
									!hasValue && 'text-muted-foreground'
								)}
							/>
						</div>
					</div>
				)}
			</PopoverTrigger>
			<PopoverContent className='relative top-6 right-4 rounded-2xl w-auto p-0' align='start'>
				<div className='relative' onFocus={handleInputRangeFocus}>
					<ReactDateRangePicker
						ranges={[dateRange]}
						onChange={handleDateSelect}
						direction='horizontal'
						moveRangeOnFirstSelection={false}
						disabledDay={isDateDisabled}
						maxDate={new Date()}
						editableDateInputs={true}
					/>
					{/* <div className="absolute bottom-1 left-0 -right-0.5 flex justify-end border-gray-100 px-4 py-2">
            <Button
              variant="outline"
              className="text-xs rounded-lg"
              size="sm"
              disabled={!hasValue}
              onClick={() => {
                onChange?.({ startDate: undefined, endDate: undefined });
                setDateRange({ key: "selection", startDate: new Date(), endDate: new Date() });
                setCalendarViewState("initial");
              }}
            >
              Clear
            </Button>
          </div> */}
				</div>
			</PopoverContent>
		</Popover>
	);
}
